import type { DocumentResult } from '@creanizer/types'

// レスポンスのパース処理
export function parseDocumentResponse(response: any, documentType: string): DocumentResult {
  try {
    // すでにパースされている場合はそのまま返す
    if (typeof response === 'object' && response.type) {
      return response as DocumentResult
    }
    
    // 文字列の場合はJSONパース
    if (typeof response === 'string') {
      const cleaned = cleanJsonResponse(response)
      const parsed = JSON.parse(cleaned)
      return validateAndCastDocument(parsed, documentType)
    }
    
    // その他の場合は直接検証
    return validateAndCastDocument(response, documentType)
  } catch (error: any) {
    console.error('[Document Parser] Failed to parse response:', error)
    
    // 部分救済を試みる
    const partialResult = tryPartialRecovery(response, documentType)
    if (partialResult) {
      return partialResult
    }
    
    throw new Error(`文献の解析に失敗しました: ${error.message}`)
  }
}

// JSONレスポンスのクリーニング
function cleanJsonResponse(text: string): string {
  // コードブロックの除去
  const codeBlockMatch = text.match(/```(?:json)?\s*([\s\S]*?)```/)
  if (codeBlockMatch) {
    text = codeBlockMatch[1]
  }
  
  // 先頭・末尾の空白を除去
  text = text.trim()
  
  // BOMの除去
  if (text.charCodeAt(0) === 0xFEFF) {
    text = text.slice(1)
  }
  
  // 制御文字の除去
  text = text.replace(/[\x00-\x1F\x7F-\x9F]/g, '')
  
  // エスケープされていない改行をエスケープ
  text = text.replace(/\n/g, '\\n')
  text = text.replace(/\r/g, '\\r')
  text = text.replace(/\t/g, '\\t')
  
  return text
}

// 文献タイプ別の検証と型変換
function validateAndCastDocument(parsed: any, documentType: string): DocumentResult {
  // 基本的なメタデータを追加
  const metadata = {
    generatedAt: new Date().toISOString(),
    documentType,
    wordCount: calculateWordCount(parsed)
  }
  
  // typeフィールドを設定
  if (!parsed.type) {
    parsed.type = documentType
  }
  
  // metadataフィールドを設定
  if (!parsed.metadata) {
    parsed.metadata = metadata
  } else {
    parsed.metadata = { ...metadata, ...parsed.metadata }
  }
  
  return parsed as DocumentResult
}

// 文字数カウント
function calculateWordCount(doc: any): number {
  let count = 0
  
  // 再帰的に文字列を探してカウント
  const countStrings = (obj: any): void => {
    if (typeof obj === 'string') {
      count += obj.length
    } else if (Array.isArray(obj)) {
      obj.forEach(countStrings)
    } else if (obj && typeof obj === 'object') {
      Object.values(obj).forEach(countStrings)
    }
  }
  
  countStrings(doc)
  return count
}

// 部分的な復旧を試みる
function tryPartialRecovery(response: any, documentType: string): DocumentResult | null {
  try {
    const text = typeof response === 'string' ? response : JSON.stringify(response)
    
    // 最低限の構造を作成
    const baseResult = {
      type: documentType,
      metadata: {
        generatedAt: new Date().toISOString(),
        documentType,
        wordCount: 0
      }
    }
    
    // タイプ別の部分復旧
    switch (documentType) {
      case 'sns_twitter':
        // ツイート本文だけでも抽出
        const tweetMatches = text.match(/"text"\s*:\s*"([^"]+)"/g) || []
        if (tweetMatches.length > 0) {
          const posts = tweetMatches.map(match => {
            const textContent = match.match(/"text"\s*:\s*"([^"]+)"/)
            return {
              text: textContent ? textContent[1] : '',
              author: {
                username: 'user',
                displayName: 'User',
                verified: false
              },
              timestamp: new Date().toISOString(),
              engagement: {
                likes: 0,
                retweets: 0,
                replies: 0
              }
            }
          })
          return {
            ...baseResult,
            type: 'sns_twitter',
            posts
          } as any
        }
        break
        
      case 'diary':
        // 本文だけでも抽出
        const bodyMatch = text.match(/"body"\s*:\s*"([^"]+)"/)
        if (bodyMatch) {
          return {
            ...baseResult,
            type: 'diary',
            date: new Date().toLocaleDateString(),
            body: bodyMatch[1]
          } as any
        }
        break
        
      case 'newspaper':
        // 見出しと本文を抽出
        const headlineMatch = text.match(/"headline"\s*:\s*"([^"]+)"/)
        const newsBodyMatch = text.match(/"body"\s*:\s*"([^"]+)"/)
        if (headlineMatch) {
          return {
            ...baseResult,
            type: 'newspaper',
            publication: '不明',
            date: new Date().toISOString(),
            headline: headlineMatch[1],
            body: newsBodyMatch ? newsBodyMatch[1] : '',
            author: { name: '記者' }
          } as any
        }
        break
    }
  } catch (error) {
    console.error('[Document Parser] Partial recovery failed:', error)
  }
  
  return null
}